/**
 * useMisreadReport — SC-027 신고 시트를 여는 손잡이.
 *
 * 진입점이 셋(SC-010 · SC-011 · SC-002)인데 **여는 조건은 하나**라 여기 모았다:
 * 비회원이면 신고 대신 로그인으로 보낸다.
 *
 * ⚠ 막는 자리가 **누른 순간**인 것이 중요하다. 시트를 열어 주고 보내기에서
 *   막으면, 유형을 고르고 정답 카드까지 짚어 놓은 것이 통째로 사라진다.
 *   할 수 없는 일은 시작하기 전에 말한다.
 * ⚠ '아직 모르는 상태'가 있다. accessToken 은 메모리에만 있어 콜드스타트 직후
 *   /auth/refresh 가 끝나기 전까지 null 이다 — 그 창에서 판단하면 **로그인한
 *   사람을 로그인 화면으로 보낸다.** bootstrapped 로 그 창을 가려 낸다.

 */
import { useCallback, useState } from "react";
import { useAuthStore } from "../../store/authStore";
import { useRequireAuth } from "../../navigation/useRequireAuth";

export interface MisreadReportApi {
  /** 지금 신고 중인 카드. null 이면 시트가 닫혀 있다 */
  reportingUid: string | null;
  /** 카드 행의 '신고' 가 부른다. 비회원이면 대신 로그인으로 보낸다 */
  openReport: (cardUid: string) => void;
  closeReport: () => void;
}

export function useMisreadReport(): MisreadReportApi {
  const [reportingUid, setReportingUid] = useState<string | null>(null);
  const hasToken = useAuthStore((s) => !!s.accessToken);
  const bootstrapped = useAuthStore((s) => s.bootstrapped);
  const { requireAuth } = useRequireAuth();

  const openReport = useCallback(
    (cardUid: string) => {
      /* 부트스트랩 전에는 아직 모른다 — 모르는 채로 막지 않는다.
         requireAuth 가 그 시점의 진짜 토큰을 다시 본다. */
      if (hasToken || !bootstrapped) {
        setReportingUid(cardUid);
        return;
      }
      requireAuth({ type: "none" });
    },
    [hasToken, bootstrapped, requireAuth],
  );

  return {
    reportingUid,
    openReport,
    closeReport: useCallback(() => setReportingUid(null), []),
  };
}
